import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

// ---------------------------------------------------------------------------
// RESEARCH
// Content lives in `content/research/<slug>/index.md`. Each file's frontmatter
// (title, role, lab, range, location, tech) plus its markdown body (bullet
// points) renders as one card. Add a folder to add an entry; newest `date`
// renders first.
// ---------------------------------------------------------------------------
const StyledResearchSection = styled.section`
  max-width: 900px;
`;

const StyledResearchCard = styled.div`
  ${({ theme }) => theme.mixins.boxShadow};
  padding: 2rem 2.25rem;
  border-radius: var(--border-radius);
  background-color: var(--light-navy);
  transition: var(--transition);

  &:not(:last-of-type) {
    margin-bottom: 25px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }

  .research-title {
    margin: 0 0 6px;
    color: var(--lightest-slate-org);
    font-size: var(--fz-xxl);
    font-weight: 600;
    line-height: 1.3;
  }

  .research-lab {
    margin: 0;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
  }

  .research-range {
    margin: 4px 0 20px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  .research-highlights ul {
    ${({ theme }) => theme.mixins.fancyList};
    margin: 0;
  }

  ul.research-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0;
    margin: 22px 0 0;
    list-style: none;

    li {
      color: var(--green);
      background-color: var(--green-tint);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      padding: 5px 11px;
      border-radius: 20px;
    }
  }
`;

const Research = () => {
  const data = useStaticQuery(graphql`
    query {
      research: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/research/" } }
        sort: { frontmatter: { date: DESC } }
      ) {
        edges {
          node {
            frontmatter {
              title
              role
              lab
              range
              location
              tech
            }
            html
          }
        }
      }
    }
  `);

  const researchItems = data.research.edges;

  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  return (
    <StyledResearchSection id="research" ref={revealContainer}>
      <h2 className="numbered-heading">Research</h2>

      {researchItems &&
        researchItems.map(({ node }, i) => {
          const { frontmatter, html } = node;
          const { title, role, lab, range, location, tech } = frontmatter;

          return (
            <StyledResearchCard key={i}>
              <h3 className="research-title">{title}</h3>
              <p className="research-lab">
                {role} · {lab}
              </p>
              <p className="research-range">
                {range}
                {location ? ` · ${location}` : ''}
              </p>

              <div
                className="research-highlights"
                dangerouslySetInnerHTML={{ __html: html }}
              />

              {tech && tech.length > 0 && (
                <ul className="research-tags">
                  {tech.map((tag, j) => (
                    <li key={j}>{tag}</li>
                  ))}
                </ul>
              )}
            </StyledResearchCard>
          );
        })}
    </StyledResearchSection>
  );
};

export default Research;
